import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { FiltersScopeProvider } from "~/components/filters/FiltersScope";
import type { IEventMarketsFilters } from "~/feature/event/types/eventMarketsFilters";
import { deepEqual } from "~/utils/deepEqual";

export type EventMarketsFiltersState = {
  [K in keyof IEventMarketsFilters]?: IEventMarketsFilters[K];
};

interface IEventMarketsFiltersContextValue {
  filters: EventMarketsFiltersState;
  updateFilters: (value: EventMarketsFiltersState) => void;
  setAllFilters: (value: EventMarketsFiltersState) => void;
  clearAllFilters: () => void;
  isFiltersApplied: boolean;
  registerOnClearAllFilters: (cb: () => void) => () => void;
}

const defaultFilters: EventMarketsFiltersState = {};

const EventMarketsFiltersContext =
  createContext<IEventMarketsFiltersContextValue | null>(null);

interface IEventMarketsFiltersProviderProps {
  children: ReactNode;
}

export const EventMarketsFiltersProvider = ({
  children,
}: IEventMarketsFiltersProviderProps) => {
  const [filters, setFiltersState] =
    useState<EventMarketsFiltersState>(defaultFilters);
  const onClearAllFiltersCallbacks = useRef<Set<() => void>>(new Set());

  const registerOnClearAllFilters = useCallback((cb: () => void) => {
    const callbacks = onClearAllFiltersCallbacks.current;
    callbacks.add(cb);
    return () => {
      callbacks.delete(cb);
    };
  }, []);

  const updateFilters = useCallback((value: EventMarketsFiltersState) => {
    setFiltersState((prev) => ({ ...prev, ...value }));
  }, []);

  const setAllFilters = useCallback((value: EventMarketsFiltersState) => {
    setFiltersState(value);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFiltersState({ ...defaultFilters });
    onClearAllFiltersCallbacks.current.forEach((cb) => cb());
  }, []);

  const isFiltersApplied = useMemo(() => {
    const filteredAll = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== undefined)
    );

    return !deepEqual(filteredAll, defaultFilters);
  }, [filters]);

  const value = useMemo(
    () => ({
      filters,
      updateFilters,
      setAllFilters,
      clearAllFilters,
      isFiltersApplied,
      registerOnClearAllFilters,
    }),
    [
      filters,
      updateFilters,
      setAllFilters,
      clearAllFilters,
      isFiltersApplied,
      registerOnClearAllFilters,
    ]
  );

  return (
    <EventMarketsFiltersContext.Provider value={value}>
      <FiltersScopeProvider value={value}>{children}</FiltersScopeProvider>
    </EventMarketsFiltersContext.Provider>
  );
};

export const useEventMarketsFilter = (): IEventMarketsFiltersContextValue => {
  const context = useContext(EventMarketsFiltersContext);

  if (!context) {
    throw new Error(
      "useEventMarketsFilter must be used within EventMarketsFiltersProvider"
    );
  }

  return context;
};

export const useOptionalEventMarketsFilter =
  (): IEventMarketsFiltersContextValue | null =>
    useContext(EventMarketsFiltersContext);
